import { useState, useEffect, useCallback } from 'react';

// Global cache for BTC price
interface BtcPriceCache {
  price: number;
  timestamp: number;
}

// Cache stored in memory (survives across component mounts/unmounts during page session)
let globalCache: BtcPriceCache | null = null;

// Track active fetch promise to prevent duplicate requests
let activeFetch: Promise<number | null> | null = null;

// Cache duration: 5 minutes (300000ms)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Custom hook to fetch and cache Bitcoin price from CoinGecko API.
 * 
 * Features:
 * - Caches price for 5 minutes to minimize API calls
 * - Shares cache across all hook instances
 * - Prevents duplicate simultaneous requests
 * - Only fetches once per page load unless cache expires
 * - Provides loading state for UI feedback
 */
export function useBtcPrice() {
  const [btcPrice, setBtcPrice] = useState<number | null>(globalCache?.price || null);
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBtcPrice = useCallback(async (force = false): Promise<number | null> => {
    // Check if we have a valid cached price (unless forced refresh)
    if (!force && globalCache) {
      const age = Date.now() - globalCache.timestamp;
      if (age < CACHE_DURATION) {
        // Cache is still fresh, use it
        setBtcPrice(globalCache.price);
        return globalCache.price;
      }
    }

    // If there's already an active fetch, wait for it instead of creating a new one
    if (activeFetch) {
      const result = await activeFetch;
      if (result !== null) {
        setBtcPrice(result);
      }
      return result;
    }

    // Create a new fetch promise
    setIsLoadingRate(true);
    setError(null);

    activeFetch = (async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.bitcoin?.usd) {
          const price = data.bitcoin.usd;
          
          // Update global cache
          globalCache = {
            price,
            timestamp: Date.now(),
          };

          setBtcPrice(price);
          setIsLoadingRate(false);
          setError(null);
          
          return price;
        } else {
          throw new Error('Invalid response format from CoinGecko API');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch BTC price';
        console.error('BTC price fetch error:', errorMessage);
        
        setError(errorMessage);
        setIsLoadingRate(false);
        
        return null;
      } finally {
        // Clear the active fetch promise
        activeFetch = null;
      }
    })();

    return activeFetch;
  }, []);

  // Fetch price on mount if cache is stale or empty
  useEffect(() => {
    fetchBtcPrice();
  }, [fetchBtcPrice]);

  /**
   * Convert USD amount to satoshis
   */
  const usdToSats = useCallback((usdAmount: number): number => {
    if (!btcPrice || usdAmount <= 0) return 0;
    return Math.round((usdAmount / btcPrice) * 100_000_000);
  }, [btcPrice]);

  /**
   * Convert satoshis to USD amount
   */
  const satsToUsd = useCallback((sats: number): number => {
    if (!btcPrice || sats <= 0) return 0;
    return (sats / 100_000_000) * btcPrice;
  }, [btcPrice]);

  /**
   * Get cache age in milliseconds
   */
  const getCacheAge = useCallback((): number | null => {
    if (!globalCache) return null;
    return Date.now() - globalCache.timestamp;
  }, []);

  /**
   * Check if cache is stale
   */
  const isCacheStale = useCallback((): boolean => {
    const age = getCacheAge();
    return age === null || age >= CACHE_DURATION;
  }, [getCacheAge]);

  /**
   * Force refresh the price (ignores cache)
   */
  const refreshPrice = useCallback(() => {
    return fetchBtcPrice(true);
  }, [fetchBtcPrice]);

  return {
    btcPrice,
    isLoadingRate,
    error,
    usdToSats,
    satsToUsd,
    getCacheAge,
    isCacheStale,
    refreshPrice,
  };
}
